import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Tooltip } from "../../../../utils/local-antd";

import { Button } from "../../../../components/Button";
import { Flex } from "../../../../components/Flex";
import { Info } from "../../../../components/Info";
import { Input } from "../../../../components/Input";
import { Modal } from "../../../../components/modal/Modal";
import { Text } from "../../../../components/Text";
import { MetaMessageTemplate } from "../../../../model/integration/dto/meta/MetaMessageTemplate";
import { MetaTemplateParameters } from "../../../../model/integration/dto/meta/MetaTemplateParameters";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { ThemeType } from "../../../../theme/Theme";
import { WATemplateContent } from "./components/WATemplateContent";
import {
  templateParametersFrom,
  templateVariables,
  variableReady,
} from "./utils/templateVariables";

const Columns = styled(Flex)`
  gap: ${Spacing.m};
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const List = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  flex: 1;
  max-height: 60vh;
  overflow-y: auto;
  padding-inline-end: ${Spacing.xs};
`;

const Row = styled(Flex)<{ $selected: boolean; $disabled: boolean }>`
  flex-direction: column;
  gap: ${Spacing.xs};
  padding: ${Spacing.s};
  border-radius: ${Rounded.md};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.main : theme.lightgray)};
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.lightSub : theme.white};
`;

/** The template as the customer gets it, and nothing of ours inside the box -
 * an input or a send button in there reads as part of the message.
 */
const Preview = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  padding: ${Spacing.m};
  border: 1px solid ${({ theme }) => theme.lightgray};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.lightSub};
  max-height: 50vh;
  overflow-y: auto;
`;

const Dot = styled.span<{ $color: keyof ThemeType }>`
  width: 0.6rem;
  height: 0.6rem;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme, $color }) => theme[$color]};
  flex-shrink: 0;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  templates: MetaMessageTemplate[];
  sending?: boolean;
  onSend: (
    template: MetaMessageTemplate,
    parameters?: MetaTemplateParameters,
  ) => Promise<void> | void;
};

const QUALITY_COLOR: Record<string, keyof ThemeType> = {
  GREEN: "success",
  YELLOW: "warning",
  RED: "danger",
};

const STATUS_COLOR: Record<string, keyof ThemeType> = {
  PENDING: "warning",
  PAUSED: "warning",
  REJECTED: "danger",
  DISABLED: "danger",
};

/** The light beside a template's name. One that can't be sent shows why; an
 * approved one shows how recipients take it. Meta rates a template only after
 * enough sends, so an unrated one has had no complaints yet and reads as fine.
 */
const standingOf = (template: MetaMessageTemplate) => {
  if (template.status !== "APPROVED") {
    return {
      color: STATUS_COLOR[template.status] || "lightgray",
      labelKey: `integration.whatsapp.meta.templates.status.${template.status.toLowerCase()}`,
    };
  }

  const quality = template.quality_score?.score || "UNKNOWN";

  return {
    color: QUALITY_COLOR[quality] || "success",
    labelKey: `integration.whatsapp.meta.templates.quality.${quality.toLowerCase()}`,
  };
};

const languageName = (code: string, uiLanguage: string) => {
  try {
    return (
      new Intl.DisplayNames([uiLanguage], { type: "language" }).of(
        code.replace("_", "-"),
      ) || code
    );
  } catch {
    return code;
  }
};

export const WATemplateSendModal: React.FC<Props> = ({
  open,
  onClose,
  templates,
  sending,
  onSend,
}) => {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string>();
  const [values, setValues] = useState<Record<string, string>>({});

  const { t, i18n } = useTranslation();

  const selected = templates.find((template) => template.id === selectedId);

  const variables = useMemo(() => templateVariables(selected), [selected]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return templates.filter((template) =>
      term ? template.name.toLowerCase().includes(term) : true,
    );
  }, [templates, search]);

  // A template picked last time, half-filled, has nothing to do with the next
  // chat this opens on
  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelectedId(undefined);
      setValues({});
    }
  }, [open]);

  const missing = variables.some(
    (variable) => !variableReady(variable, values[variable.key]),
  );

  const onSelect = (template: MetaMessageTemplate) => {
    if (template.status !== "APPROVED") {
      return;
    }

    setSelectedId(template.id);
    setValues({});
  };

  const onConfirm = async () => {
    if (!selected || missing) {
      return;
    }

    await onSend(selected, templateParametersFrom(variables, values));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={900}
      title={t("integration.whatsapp.meta.templates.modal.title")}
    >
      <Flex flexDirection="column" gap="m">
        <Info info={t("integration.whatsapp.meta.templates.modal.info")} />

        <Columns>
          <Flex flexDirection="column" gap="s" flex={1}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t(
                "integration.whatsapp.meta.templates.modal.search",
              )}
            />

            <List>
              {filtered.map((template) => {
                const standing = standingOf(template);

                return (
                  <Row
                    key={template.id}
                    $selected={template.id === selectedId}
                    $disabled={template.status !== "APPROVED"}
                    onClick={() => onSelect(template)}
                  >
                    <Flex
                      alignItems="center"
                      gap="s"
                      justifyContent="space-between"
                    >
                      <Text fontSize="s" wordBreak="break-word">
                        {template.name}
                      </Text>

                      <Tooltip title={t(standing.labelKey)}>
                        <Dot $color={standing.color} />
                      </Tooltip>
                    </Flex>

                    <Flex alignItems="center" gap="s">
                      <Text fontSize="xs" color="darkerSub">
                        {languageName(template.language, i18n.language)} ·{" "}
                        {t(
                          `integration.whatsapp.meta.templates.category.${template.category}`,
                          { defaultValue: template.category },
                        )}
                      </Text>

                      {template.status !== "APPROVED" ? (
                        <Tooltip title={template.rejected_reason}>
                          <Text
                            fontSize="xs"
                            color={
                              template.status === "PENDING"
                                ? "warning"
                                : "danger"
                            }
                          >
                            {t(
                              `integration.whatsapp.meta.templates.status.${template.status.toLowerCase()}`,
                            )}
                          </Text>
                        </Tooltip>
                      ) : null}
                    </Flex>
                  </Row>
                );
              })}

              {!filtered.length ? (
                <Text fontSize="s" color="darkerSub">
                  {t("integration.whatsapp.meta.templates.modal.empty")}
                </Text>
              ) : null}
            </List>
          </Flex>

          <Flex flexDirection="column" gap="m" flex={1.2}>
            <Preview>
              {selected ? (
                <WATemplateContent
                  components={selected.components}
                  values={values}
                />
              ) : (
                <Text fontSize="s" color="darkerSub">
                  {t("integration.whatsapp.meta.templates.modal.pick")}
                </Text>
              )}
            </Preview>

            {selected ? (
              <>
                {variables.length ? (
                  <Flex flexDirection="column" gap="s">
                    {variables.map((variable) => (
                      <Input
                        key={variable.key}
                        value={values[variable.key] || ""}
                        onChange={(e) =>
                          setValues((current) => ({
                            ...current,
                            [variable.key]: e.target.value,
                          }))
                        }
                        title={t(
                          variable.area === "location"
                            ? `integration.whatsapp.meta.templates.modal.variable.location.${variable.name}`
                            : `integration.whatsapp.meta.templates.modal.variable.${variable.area}`,
                          { name: variable.name },
                        )}
                        placeholder={
                          variable.example ||
                          t(
                            "integration.whatsapp.meta.templates.modal.variable.placeholder",
                          )
                        }
                        required={!variable.optional}
                      />
                    ))}
                  </Flex>
                ) : null}

                <Button
                  onClick={onConfirm}
                  loading={sending}
                  disabled={missing}
                  info={
                    missing
                      ? t(
                          "integration.whatsapp.meta.templates.modal.fill_first",
                        )
                      : undefined
                  }
                >
                  {t("integration.whatsapp.meta.templates.modal.send")}
                </Button>
              </>
            ) : null}
          </Flex>
        </Columns>
      </Flex>
    </Modal>
  );
};
